import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { twilioA2pApi } from "../lib/api";
import {
  MessageSquareText, Loader2, Send, ShieldCheck, Building2, MapPin, User,
  Landmark, CheckCircle2, XCircle, Clock, AlertTriangle,
} from "lucide-react";

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";
const selectClass = `${inputClass} appearance-none pr-7 cursor-pointer`;

// Twilio's exact Trust Hub enum values — labels are friendlier, values are
// the literal strings the API requires.
const BUSINESS_TYPES = [
  { value: "Corporation", label: "Corporation" },
  { value: "Limited Liability Corporation", label: "LLC" },
  { value: "Sole Proprietorship", label: "Sole Proprietor" },
  { value: "Non-profit Corporation", label: "Nonprofit" },
  { value: "Partnership", label: "Partnership" },
  { value: "Co-operative", label: "Other" },
];

const BUSINESS_INDUSTRIES = [
  "AGRICULTURE", "AUTOMOTIVE", "BANKING", "CONSUMER", "EDUCATION", "ELECTRONICS",
  "ENERGY", "ENGINEERING", "FAST_MOVING_CONSUMER_GOODS", "FINANCIAL", "FINTECH",
  "FOOD_AND_BEVERAGE", "GOVERNMENT", "HEALTHCARE", "HOSPITALITY", "INSURANCE",
  "JEWELRY", "LEGAL", "MANUFACTURING", "MEDIA", "NOT_FOR_PROFIT", "OIL_AND_GAS",
  "ONLINE", "RAW_MATERIALS", "REAL_ESTATE", "RELIGION", "RETAIL", "TECHNOLOGY",
  "TELECOMMUNICATIONS", "TRAVEL",
].map((v) => ({ value: v, label: v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }));

const JOB_POSITIONS = ["Director", "GM", "VP", "CEO", "CFO", "General Counsel", "Other"];

const COMPANY_TYPES = [
  { value: "private", label: "Private" },
  { value: "public", label: "Public" },
  { value: "non_profit", label: "Nonprofit" },
  { value: "government", label: "Government" },
];

const EMPTY_FORM = {
  legalBusinessName: "", businessType: "Corporation", businessIndustry: "RETAIL",
  ein: "", websiteUrl: "", businessRegionsOfOperation: "USA_AND_CANADA",
  street: "", streetLine2: "", city: "", state: "", postalCode: "", country: "US",
  repFirstName: "", repLastName: "", repJobTitle: "CEO", repBusinessTitle: "",
  repEmail: "", repPhone: "",
  companyType: "private", stockExchange: "", stockTicker: "",
};

function statusLabel(raw) {
  if (!raw) return { text: "Not Started", tone: "slate" };
  if (raw === "noncompliant") return { text: "Noncompliant", tone: "red" };
  if (raw === "twilio-approved") return { text: "Approved", tone: "green" };
  if (raw === "twilio-rejected" || raw === "rejected") return { text: "Failed", tone: "red" };
  return { text: "Pending Review", tone: "amber" };
}

function brandStatusLabel(raw) {
  if (!raw) return { text: "Not Started", tone: "slate" };
  const map = { PENDING: "Pending", APPROVED: "Approved", FAILED: "Failed", IN_REVIEW: "In Review" };
  const tone = raw === "APPROVED" ? "green" : raw === "FAILED" ? "red" : "amber";
  return { text: map[raw] || raw, tone };
}

const TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-600",
  amber: "bg-amber-100 text-amber-700",
  green: "bg-emerald-100 text-emerald-700",
  red: "bg-red-100 text-red-700",
};

const REJECTED_STATUSES = ["twilio-rejected", "rejected", "noncompliant"];

function isRejectedReg(reg) {
  if (!reg) return false;
  return (
    reg.status === "failed_compliance" ||
    reg.status === "noncompliant" ||
    REJECTED_STATUSES.includes(reg.customerProfileStatus) ||
    REJECTED_STATUSES.includes(reg.trustProductStatus) ||
    reg.brandStatus === "FAILED"
  );
}

function rejectionReason(reg) {
  if (!reg) return null;
  return (
    reg.brandFailureReason ||
    reg.trustProductError ||
    reg.customerProfileError ||
    reg.error?.message ||
    null
  );
}

function StatusPill({ tone, text }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold ${TONE_CLASSES[tone]}`}>{text}</span>;
}

function EvaluationErrors({ results }) {
  const failed = (results || []).filter((r) => !r.passed);
  if (!failed.length) return null;
  return (
    <ul className="mt-2 space-y-1">
      {failed.map((r, i) => (
        <li key={i} className="text-[12px] text-red-700">
          <span className="font-semibold">{r.friendly_name}:</span>{" "}
          {(r.fields || []).filter((f) => !f.passed).map((f) => f.failure_reason || `${f.friendly_name} is invalid`).join("; ")}
        </li>
      ))}
    </ul>
  );
}

export default function A2PRegistrationCard() {
  const [loading, setLoading] = useState(true);
  const [reg, setReg] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [campaignSubmitting, setCampaignSubmitting] = useState(false);
  const [campaignError, setCampaignError] = useState(null);
  const pollRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const data = await twilioA2pApi.status();
      setReg(data && data.customerProfileSid ? data : null);
      if (data && data.form) {
        const sanitized = Object.fromEntries(Object.entries(data.form).map(([k, v]) => [k, v == null ? "" : v]));
        setForm((f) => ({ ...f, ...sanitized }));
      }
    } catch {
      // no registration yet / not fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 60000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await twilioA2pApi.register(form);
      setReg(data);
      toast.success("A2P registration submitted");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSubmitError(typeof detail === "string" ? { message: detail } : detail || { message: "Registration failed." });
      // Refresh so partial progress (SIDs created before the failure) is reflected
      load();
    } finally {
      setSubmitting(false);
    }
  };

  const registerCampaigns = async () => {
    setCampaignSubmitting(true);
    setCampaignError(null);
    try {
      const data = await twilioA2pApi.registerCampaigns();
      setReg(data);
      toast.success("Campaign submitted for carrier review");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setCampaignError(typeof detail === "string" ? { message: detail } : detail || { message: "Campaign registration failed." });
      load();
    } finally {
      setCampaignSubmitting(false);
    }
  };

  const retryRegistration = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await twilioA2pApi.register(form);
      setReg(data);
      toast.success("Registration resumed");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSubmitError(typeof detail === "string" ? { message: detail } : detail || { message: "Retry failed." });
      load();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-center h-24" data-testid="a2p-card-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const showForm = !reg || isRejectedReg(reg);
  const showTracker = !!reg;
  const isResubmit = !!reg && isRejectedReg(reg);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="a2p-registration-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4.5 w-4.5 text-blue-600" />
        <h3 className="text-[14.5px] font-bold text-slate-900">A2P Text Messaging Registration</h3>
      </div>
      <p className="text-[12.5px] text-slate-500 mb-4">
        Required by US carriers before you can send SMS/MMS at scale. Registers your business identity with Twilio
        via your company's Twilio subaccount — this only needs to be done once.
      </p>

      {showForm && (
        <form onSubmit={submit} className="space-y-5" data-testid="a2p-registration-form">
          {isResubmit && !submitError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="a2p-resubmit-banner">
              <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" /> Your previous submission was flagged by Twilio. Review the details below, correct anything needed, and resubmit.
              </div>
              {rejectionReason(reg) && (
                <div className="mt-1.5 text-[12px] text-amber-700" data-testid="a2p-resubmit-reason">
                  Reason: {rejectionReason(reg)}
                </div>
              )}
            </div>
          )}
          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="a2p-submit-error">
              <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-red-700">
                <AlertTriangle className="h-3.5 w-3.5" /> {submitError.message}
              </div>
              {Array.isArray(submitError.details) && <EvaluationErrors results={submitError.details} />}
            </div>
          )}

          {/* Business Information */}
          <div>
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 mb-2.5"><Building2 className="h-3.5 w-3.5 text-slate-400" /> Business Information</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Legal Business Name">
                <input required value={form.legalBusinessName} onChange={(e) => set("legalBusinessName", e.target.value)} data-testid="a2p-legal-business-name" className={inputClass} />
              </Field>
              <Field label="Business Type">
                <select required value={form.businessType} onChange={(e) => set("businessType", e.target.value)} data-testid="a2p-business-type" className={selectClass}>
                  {BUSINESS_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Business Industry">
                <select required value={form.businessIndustry} onChange={(e) => set("businessIndustry", e.target.value)} data-testid="a2p-business-industry" className={selectClass}>
                  {BUSINESS_INDUSTRIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="EIN / Business Registration Number">
                <input required value={form.ein} onChange={(e) => set("ein", e.target.value)} data-testid="a2p-ein" className={inputClass} />
              </Field>
              <Field label="Business Website URL">
                <input required type="url" placeholder="https://" value={form.websiteUrl} onChange={(e) => set("websiteUrl", e.target.value)} data-testid="a2p-website-url" className={inputClass} />
              </Field>
              <Field label="Business Regions of Operation">
                <input readOnly value="USA & Canada" data-testid="a2p-regions" className={`${inputClass} bg-slate-50 text-slate-500`} />
              </Field>
            </div>
          </div>

          {/* Business Address */}
          <div>
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 mb-2.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> Business Address</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Street Address">
                <input required value={form.street} onChange={(e) => set("street", e.target.value)} data-testid="a2p-street" className={inputClass} />
              </Field>
              <Field label="Street Address Line 2 (optional)">
                <input value={form.streetLine2} onChange={(e) => set("streetLine2", e.target.value)} data-testid="a2p-street-2" className={inputClass} />
              </Field>
              <Field label="City">
                <input required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="a2p-city" className={inputClass} />
              </Field>
              <Field label="State / Region">
                <input required value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="a2p-state" className={inputClass} />
              </Field>
              <Field label="Postal Code">
                <input required value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} data-testid="a2p-postal-code" className={inputClass} />
              </Field>
              <Field label="Country">
                <input required value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="a2p-country" className={inputClass} />
              </Field>
            </div>
          </div>

          {/* Authorized Representative */}
          <div>
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 mb-2.5"><User className="h-3.5 w-3.5 text-slate-400" /> Authorized Representative</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="First Name">
                <input required value={form.repFirstName} onChange={(e) => set("repFirstName", e.target.value)} data-testid="a2p-rep-first-name" className={inputClass} />
              </Field>
              <Field label="Last Name">
                <input required value={form.repLastName} onChange={(e) => set("repLastName", e.target.value)} data-testid="a2p-rep-last-name" className={inputClass} />
              </Field>
              <Field label="Job Title">
                <select required value={form.repJobTitle} onChange={(e) => set("repJobTitle", e.target.value)} data-testid="a2p-rep-job-title" className={selectClass}>
                  {JOB_POSITIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Business Title">
                <input required placeholder="e.g. Owner, President" value={form.repBusinessTitle} onChange={(e) => set("repBusinessTitle", e.target.value)} data-testid="a2p-rep-business-title" className={inputClass} />
              </Field>
              <Field label="Email">
                <input required type="email" value={form.repEmail} onChange={(e) => set("repEmail", e.target.value)} data-testid="a2p-rep-email" className={inputClass} />
              </Field>
              <Field label="Phone Number">
                <input required type="tel" placeholder="+15125551234" value={form.repPhone} onChange={(e) => set("repPhone", e.target.value)} data-testid="a2p-rep-phone" className={inputClass} />
              </Field>
            </div>
          </div>

          {/* Company Type (messaging profile) */}
          <div>
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 mb-2.5"><Landmark className="h-3.5 w-3.5 text-slate-400" /> Company Type</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Company Type">
                <select required value={form.companyType} onChange={(e) => set("companyType", e.target.value)} data-testid="a2p-company-type" className={selectClass}>
                  {COMPANY_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              {form.companyType === "public" && (
                <>
                  <Field label="Stock Exchange">
                    <input required value={form.stockExchange} onChange={(e) => set("stockExchange", e.target.value)} data-testid="a2p-stock-exchange" className={inputClass} />
                  </Field>
                  <Field label="Stock Ticker">
                    <input required value={form.stockTicker} onChange={(e) => set("stockTicker", e.target.value)} data-testid="a2p-stock-ticker" className={inputClass} />
                  </Field>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button type="submit" disabled={submitting} data-testid="a2p-submit-btn" className="h-10 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold">
              {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {submitting ? "Registering…" : isResubmit ? "Resubmit Registration" : "Submit Registration"}
            </Button>
          </div>
        </form>
      )}

      {showTracker && (
        <A2PStatusTracker
          reg={reg}
          onRegisterCampaigns={registerCampaigns}
          campaignSubmitting={campaignSubmitting}
          campaignError={campaignError}
          onRetry={retryRegistration}
          retrySubmitting={submitting}
        />
      )}
    </div>
  );
}

const CAMPAIGN_ROW = {
  sidField: "campaignSid",
  statusField: "campaignStatus",
  errorField: "campaignStatusError",
  label: "Home Service Appointment Notifications",
};

function campaignRowDisplay(brandApproved, sid, rawStatus) {
  if (!brandApproved) return { text: "Awaiting Brand Approval", tone: "slate" };
  if (!sid) return { text: "Not Registered", tone: "slate" };
  if (rawStatus === "VERIFIED") return { text: "Verified", tone: "green" };
  if (rawStatus === "FAILED") return { text: "Failed", tone: "red" };
  return { text: "Pending", tone: "amber" };
}

function A2PStatusTracker({ reg, onRegisterCampaigns, campaignSubmitting, campaignError, onRetry, retrySubmitting }) {
  const profile = statusLabel(reg.customerProfileStatus);
  const trust = statusLabel(reg.trustProductStatus);
  const brand = brandStatusLabel(reg.brandStatus);
  const rows = [
    { key: "profile", label: "Secondary Customer Profile", ...profile, evaluation: reg.customerProfileEvaluation, error: reg.customerProfileError },
    { key: "trust", label: "A2P Trust Product", ...trust, evaluation: reg.trustProductEvaluation, error: reg.trustProductError },
    { key: "brand", label: "Brand Registration", ...brand, error: reg.brandFailureReason },
    { key: "service", label: "Messaging Service", text: reg.messagingServiceSid ? "Created" : "Not Created", tone: reg.messagingServiceSid ? "green" : "slate" },
  ];

  const brandApproved = reg.brandStatus === "APPROVED";
  const campaignRegistered = !!reg[CAMPAIGN_ROW.sidField];
  const canRegisterCampaigns = brandApproved && !campaignRegistered;
  const campaignDisplay = campaignRowDisplay(brandApproved, reg[CAMPAIGN_ROW.sidField], reg[CAMPAIGN_ROW.statusField]);
  const campaignErrorText = reg[CAMPAIGN_ROW.errorField];

  return (
    <div className="mt-5 pt-5 border-t border-slate-100" data-testid="a2p-status-tracker">
      <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 mb-3">
        <MessageSquareText className="h-3.5 w-3.5 text-slate-400" /> Registration Status
      </div>

      {reg.status === "interrupted" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-3" data-testid="a2p-interrupted-banner">
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Registration temporarily interrupted.
          </div>
          <div className="mt-1 text-[12px] text-amber-700">Your previous registration information has been saved.</div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={onRetry}
              disabled={retrySubmitting}
              data-testid="a2p-retry-btn"
              className="h-9 bg-amber-600 hover:bg-amber-700 text-white text-[12.5px] font-semibold"
            >
              {retrySubmitting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null} Retry Registration
            </Button>
          </div>
        </div>
      )}

      {reg.status === "failed_compliance" && reg.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-3" data-testid="a2p-tracker-error">
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-red-700">
            <XCircle className="h-3.5 w-3.5" /> Step {reg.error.step} failed: {reg.error.message}
          </div>
          {Array.isArray(reg.error.details) && <EvaluationErrors results={reg.error.details} />}
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`a2p-status-row-${r.key}`}>
            <div className="flex items-center gap-2 text-[13px] text-slate-700">
              {r.tone === "green" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : r.tone === "red" ? <XCircle className="h-3.5 w-3.5 text-red-500" /> : <Clock className="h-3.5 w-3.5 text-slate-400" />}
              {r.label}
            </div>
            <div className="text-right">
              <StatusPill tone={r.tone} text={r.text} />
              {r.evaluation?.status === "noncompliant" && <EvaluationErrors results={r.evaluation.results} />}
              {r.error && <div className="mt-1 text-[11.5px] text-red-600 max-w-xs">{typeof r.error === "string" ? r.error : JSON.stringify(r.error)}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100" data-testid="a2p-campaigns-section">
        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700 mb-3">
          <Send className="h-3.5 w-3.5 text-slate-400" /> SMS Campaign
        </div>

        {campaignError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-3" data-testid="a2p-campaign-error">
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" /> {campaignError.message}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3 py-2" data-testid="a2p-campaign-row">
            <div className="flex items-center gap-2 text-[13px] text-slate-700">
              {campaignDisplay.tone === "green" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : campaignDisplay.tone === "red" ? <XCircle className="h-3.5 w-3.5 text-red-500" /> : <Clock className="h-3.5 w-3.5 text-slate-400" />}
              {CAMPAIGN_ROW.label}
            </div>
            <div className="text-right">
              <StatusPill tone={campaignDisplay.tone} text={campaignDisplay.text} />
              {campaignErrorText && <div className="mt-1 text-[11.5px] text-red-600 max-w-xs">{campaignErrorText}</div>}
            </div>
          </div>
        </div>

        {reg.legacyCampaigns && (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="a2p-legacy-campaigns">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Legacy campaigns (read-only)</div>
            <div className="space-y-1.5">
              {Object.entries(reg.legacyCampaigns).map(([key, c]) => (
                <div key={key} className="flex items-center justify-between text-[12px] text-slate-500">
                  <span>{c.label}</span>
                  <span>{c.status || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {canRegisterCampaigns && (
          <div className="flex justify-end pt-3">
            <Button
              onClick={onRegisterCampaigns}
              disabled={campaignSubmitting}
              data-testid="a2p-register-campaigns-btn"
              className="h-10 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold"
            >
              {campaignSubmitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {campaignSubmitting ? "Registering Campaign…" : "Register Campaign"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
